<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { vOnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import SettingsAccordion from 'dashboard/components-next/Settings/SettingsAccordion.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';
import assignmentPoliciesAPI from 'dashboard/api/assignmentPolicies';
import TeamsAPI from 'dashboard/api/teams';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const selectedAgentIds = ref([]);
const isAgentListUpdating = ref(false);
const assignmentPolicy = ref(null);
const isLoadingPolicy = ref(false);
const isDeletingPolicy = ref(false);
const showDeleteConfirmModal = ref(false);
const availablePolicies = ref([]);
const isLoadingPolicies = ref(false);
const showPolicyDropdown = ref(false);
const isLinkingPolicy = ref(false);
const showTeamDropdown = ref(false);
const isAddingTeam = ref(false);

const agentList = computed(() => store.getters['agents/getAgents']);
const teamList = computed(() => store.getters['teams/getTeams']);

const selectedAgentNames = computed(() =>
  selectedAgentIds.value.map(
    id => agentList.value.find(agent => agent.id === id)?.name ?? ''
  )
);

const agentMenuItems = computed(() =>
  agentList.value
    .filter(({ id }) => !selectedAgentIds.value.includes(id))
    .map(({ id, name, thumbnail, avatar_url }) => ({
      label: name,
      value: id,
      action: 'select',
      thumbnail: { name, src: thumbnail || avatar_url || '' },
    }))
);

const teamMenuItems = computed(() =>
  teamList.value.map(team => ({
    action: 'add_team',
    value: team.id,
    label: team.name,
    icon: 'i-lucide-users',
  }))
);

const assignmentOrderLabel = computed(() => {
  const priority = assignmentPolicy.value?.conversation_priority;
  if (priority === 'longest_waiting') {
    return t('INBOX_MGMT.ASSIGNMENT.PRIORITY.LONGEST_WAITING');
  }
  return t('INBOX_MGMT.ASSIGNMENT.PRIORITY.EARLIEST_CREATED');
});

const assignmentMethodLabel = computed(() => {
  const method = assignmentPolicy.value?.assignment_order;
  if (method === 'balanced') {
    return t('INBOX_MGMT.ASSIGNMENT.METHOD.BALANCED');
  }
  return t('INBOX_MGMT.ASSIGNMENT.METHOD.ROUND_ROBIN');
});

const policyMenuItems = computed(() => [
  ...availablePolicies.value.map(policy => ({
    action: 'select_policy',
    value: policy.id,
    label: policy.name,
    icon: 'i-lucide-zap',
    policy,
  })),
  {
    action: 'view_all',
    value: 'view_all',
    label: t('INBOX_MGMT.ASSIGNMENT.VIEW_ALL_POLICIES'),
    icon: 'i-lucide-arrow-right',
  },
]);

const handleAgentAdd = ({ value }) => {
  if (!selectedAgentIds.value.includes(value))
    selectedAgentIds.value.push(value);
};

const handleAgentRemove = index => selectedAgentIds.value.splice(index, 1);

const addTeamMembers = async ({ value }) => {
  if (isAddingTeam.value) return;
  isAddingTeam.value = true;
  try {
    const { data: members } = await TeamsAPI.getAgents({ teamId: value });
    selectedAgentIds.value = [
      ...new Set([
        ...selectedAgentIds.value,
        ...members.map(member => member.id),
      ]),
    ];
    showTeamDropdown.value = false;
  } catch {
    useAlert(t('INBOX_MGMT.ADD.AGENTS.ADD_TEAM_ERROR'));
  } finally {
    isAddingTeam.value = false;
  }
};

const fetchAttachedAgents = async () => {
  try {
    const response = await store.dispatch('inboxMembers/get', {
      inboxId: props.inbox.id,
    });
    selectedAgentIds.value = response.data.payload.map(member => member.id);
  } catch {
    selectedAgentIds.value = [];
  }
};

const fetchAssignmentPolicy = async () => {
  if (!props.inbox.id) return;
  isLoadingPolicy.value = true;
  try {
    const response = await assignmentPoliciesAPI.getInboxPolicy(props.inbox.id);
    assignmentPolicy.value = response.data;
  } catch {
    assignmentPolicy.value = null;
  } finally {
    isLoadingPolicy.value = false;
  }
};

const fetchAvailablePolicies = async () => {
  isLoadingPolicies.value = true;
  try {
    const response = await assignmentPoliciesAPI.get();
    availablePolicies.value = response.data;
  } catch {
    availablePolicies.value = [];
  } finally {
    isLoadingPolicies.value = false;
  }
};

const updateAgents = async () => {
  isAgentListUpdating.value = true;
  try {
    await store.dispatch('inboxMembers/create', {
      inboxId: props.inbox.id,
      agentList: selectedAgentIds.value,
    });
    useAlert(t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'));
  } catch {
    useAlert(t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE'));
  } finally {
    isAgentListUpdating.value = false;
  }
};

const linkPolicyToInbox = async policy => {
  isLinkingPolicy.value = true;
  try {
    await assignmentPoliciesAPI.setInboxPolicy(props.inbox.id, policy.id);
    assignmentPolicy.value = policy;
    showPolicyDropdown.value = false;
    useAlert(t('INBOX_MGMT.ASSIGNMENT.LINK_SUCCESS'));
  } catch {
    useAlert(t('INBOX_MGMT.ASSIGNMENT.LINK_ERROR'));
  } finally {
    isLinkingPolicy.value = false;
  }
};

const navigateToAssignmentPolicies = () =>
  router.push({
    name: 'agent_assignment_policy_index',
    params: { accountId: route.params.accountId },
  });

const navigateToCreatePolicy = () =>
  router.push({
    name: 'agent_assignment_policy_create',
    params: { accountId: route.params.accountId },
    query: { inboxId: props.inbox.id },
  });

const navigateToAssignmentPolicyEdit = () => {
  if (!assignmentPolicy.value?.id) return;
  router.push({
    name: 'agent_assignment_policy_edit',
    params: {
      accountId: route.params.accountId,
      id: assignmentPolicy.value.id,
    },
  });
};

const handlePolicyMenuAction = ({ action, policy }) => {
  if (action === 'select_policy') linkPolicyToInbox(policy);
  if (action === 'view_all') navigateToAssignmentPolicies();
  showPolicyDropdown.value = false;
};

const togglePolicyDropdown = () => {
  showPolicyDropdown.value = !showPolicyDropdown.value;
};

const deleteAssignmentPolicy = async () => {
  isDeletingPolicy.value = true;
  try {
    await assignmentPoliciesAPI.removeInboxPolicy(props.inbox.id);
    assignmentPolicy.value = null;
    showDeleteConfirmModal.value = false;
    useAlert(t('INBOX_MGMT.ASSIGNMENT_POLICY.DELETE_SUCCESS'));
  } catch {
    useAlert(t('INBOX_MGMT.ASSIGNMENT_POLICY.DELETE_ERROR'));
  } finally {
    isDeletingPolicy.value = false;
  }
};

const loadPage = () => {
  fetchAttachedAgents();
  fetchAssignmentPolicy();
  fetchAvailablePolicies();
};

watch(() => props.inbox.id, loadPage);
onMounted(loadPage);
</script>

<template>
  <div>
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_AGENTS')"
      :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_AGENTS_SUB_TEXT')"
      class="[&>div]:!items-start"
    >
      <div
        class="rounded-xl outline outline-1 -outline-offset-1 outline-n-weak hover:outline-n-strong px-2 py-2"
      >
        <TagInput
          :model-value="selectedAgentNames"
          :placeholder="$t('INBOX_MGMT.ADD.AGENTS.PICK_AGENTS')"
          :menu-items="agentMenuItems"
          show-dropdown
          skip-label-dedup
          :auto-open-dropdown="false"
          @add="handleAgentAdd"
          @remove="handleAgentRemove"
        />
      </div>
      <div
        v-on-click-outside="() => (showTeamDropdown = false)"
        class="relative mt-3"
      >
        <NextButton
          type="button"
          :label="$t('INBOX_MGMT.ADD.AGENTS.ADD_TEAM')"
          icon="i-lucide-users"
          slate
          faded
          sm
          :is-loading="isAddingTeam"
          @click="showTeamDropdown = !showTeamDropdown"
        />
        <DropdownMenu
          v-if="showTeamDropdown"
          :menu-items="teamMenuItems"
          class="top-full ltr:left-0 rtl:right-0 z-50 mt-2 max-h-64 min-w-64"
          @action="addTeamMembers"
        />
        <p class="mt-2 mb-0 text-xs text-n-slate-10">
          {{ $t('INBOX_MGMT.ADD.AGENTS.ADD_TEAM_HELP') }}
        </p>
      </div>

      <template #extra>
        <div class="grid grid-cols-1 lg:grid-cols-8">
          <div class="col-span-1 lg:col-span-2" />
          <div class="col-span-1 lg:col-span-6 mt-4 justify-self-end">
            <NextButton
              :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
              :is-loading="isAgentListUpdating"
              @click="updateAgents"
            />
          </div>
        </div>
      </template>
    </SettingsFieldSection>

    <SettingsAccordion
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.AGENT_ASSIGNMENT')"
      class="mt-6"
    >
      <div class="p-4">
        <p class="text-body-main text-n-slate-11 mb-4">
          {{ $t('INBOX_MGMT.ASSIGNMENT.CHANNEL_LINK_DESCRIPTION') }}
        </p>

        <div v-if="assignmentPolicy" class="flex items-start gap-4">
          <div
            class="flex-shrink-0 size-10 rounded-xl bg-n-slate-3 flex items-center justify-center"
          >
            <span class="i-lucide-zap text-xl text-n-slate-11" />
          </div>
          <div class="flex-grow">
            <div class="flex items-start justify-between gap-4">
              <div>
                <h4 class="text-heading-3 text-n-slate-12 mb-1">
                  {{ assignmentPolicy.name }}
                </h4>
                <p class="text-body-main text-n-slate-11 mb-3">
                  {{ assignmentOrderLabel }} · {{ assignmentMethodLabel }}
                </p>
              </div>
              <NextButton
                icon="i-lucide-unlink"
                ghost
                ruby
                sm
                @click="showDeleteConfirmModal = true"
              />
            </div>
            <NextButton
              :label="$t('INBOX_MGMT.ASSIGNMENT.CUSTOMIZE_POLICY')"
              icon="i-lucide-arrow-right"
              trailing-icon
              link
              @click="navigateToAssignmentPolicyEdit"
            />
          </div>
        </div>

        <div v-else-if="!isLoadingPolicy" class="flex flex-wrap gap-3">
          <div
            v-if="availablePolicies.length"
            v-on-click-outside="() => (showPolicyDropdown = false)"
            class="relative"
          >
            <NextButton
              icon="i-lucide-link"
              sm
              :is-loading="isLinkingPolicy"
              @click="togglePolicyDropdown"
            >
              {{ $t('INBOX_MGMT.ASSIGNMENT.LINK_EXISTING_POLICY') }}
              <Icon icon="i-lucide-chevron-down" />
            </NextButton>
            <DropdownMenu
              v-if="showPolicyDropdown"
              class="top-full ltr:left-0 rtl:right-0 mt-2 max-w-64 max-h-72"
              :menu-items="policyMenuItems"
              :is-searching="isLoadingPolicies"
              @action="handlePolicyMenuAction"
            />
          </div>
          <NextButton
            icon="i-lucide-plus"
            :label="$t('INBOX_MGMT.ASSIGNMENT.CREATE_NEW_POLICY')"
            slate
            faded
            sm
            @click="navigateToCreatePolicy"
          />
        </div>
      </div>
    </SettingsAccordion>

    <woot-modal
      v-if="showDeleteConfirmModal"
      :show="showDeleteConfirmModal"
      :on-close="() => (showDeleteConfirmModal = false)"
    >
      <div class="p-6">
        <h3 class="text-lg font-medium text-n-slate-12 mb-4">
          {{ $t('INBOX_MGMT.ASSIGNMENT_POLICY.DELETE_CONFIRM_TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11 mb-6">
          {{ $t('INBOX_MGMT.ASSIGNMENT_POLICY.DELETE_CONFIRM_MESSAGE') }}
        </p>
        <div class="flex justify-end gap-2">
          <NextButton
            color="slate"
            :label="$t('INBOX_MGMT.ASSIGNMENT_POLICY.CANCEL')"
            @click="showDeleteConfirmModal = false"
          />
          <NextButton
            color="ruby"
            :label="$t('INBOX_MGMT.ASSIGNMENT_POLICY.CONFIRM_DELETE')"
            :is-loading="isDeletingPolicy"
            @click="deleteAssignmentPolicy"
          />
        </div>
      </div>
    </woot-modal>
  </div>
</template>
